/**
 * Prompt variable parsing.
 *
 * A variable is a token that looks like `[UPPER_CASE_WITH_UNDERSCORES]` inside
 * the prompt content. The detail page detects these and renders an input for
 * each, letting the reader fill in concrete values before copying the prompt.
 *
 * Examples matched: [TOPIC], [AUDIENCE], [PRODUCT_NAME], [CTA_2]
 * Examples NOT matched: [topic], {variable}, {{name}}, [lowercase]
 */

const VAR_RE = /\[([A-Z][A-Z0-9_]*)\]/g;

export function extractVariables(content: string): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const m of content.matchAll(VAR_RE)) {
    const name = m[1];
    if (!seen.has(name)) {
      seen.add(name);
      ordered.push(name);
    }
  }
  return ordered;
}

export function hasVariables(content: string): boolean {
  VAR_RE.lastIndex = 0;
  return VAR_RE.test(content);
}

/**
 * Substitute filled values into the content. Unfilled or blank values are left
 * as the original `[VARIABLE]` token so the output remains valid as a template.
 */
export function substituteVariables(
  content: string,
  values: Record<string, string>
): string {
  return content.replace(VAR_RE, (full, name: string) => {
    const v = values[name];
    return v && v.trim() ? v.trim() : full;
  });
}

/**
 * Break the content into alternating plain-text and token segments so the UI
 * can render tokens as styled pills instead of flat text.
 *
 *   "Write a post about [TOPIC] for [AUDIENCE]."
 *   → [
 *       {kind: "text", text: "Write a post about "},
 *       {kind: "var",  name: "TOPIC"},
 *       {kind: "text", text: " for "},
 *       {kind: "var",  name: "AUDIENCE"},
 *       {kind: "text", text: "."},
 *     ]
 */
export type PromptSegment =
  | { kind: 'text'; text: string }
  | { kind: 'var'; name: string };

export function segmentPrompt(content: string): PromptSegment[] {
  const out: PromptSegment[] = [];
  let last = 0;
  VAR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = VAR_RE.exec(content)) !== null) {
    if (m.index > last) out.push({ kind: 'text', text: content.slice(last, m.index) });
    out.push({ kind: 'var', name: m[1] });
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push({ kind: 'text', text: content.slice(last) });
  return out;
}
